import {useEffect, useState} from 'react';

import {getPost} from 'mattermost-redux/selectors/entities/posts';
import {type Post} from 'mattermost-redux/types/posts';

import {useAppDispatch, useAppSelector} from 'stores/redux_store';
import {getPostThread} from 'mattermost-redux/actions/posts';
import {selectThreadById} from 'features/threads/selectors/select_thread_by_id';
import {createThunkAction} from 'stores/create_thunk_action';
import {getCurrentUserId} from 'mattermost-redux/selectors/entities/common';
import {getCurrentTeamId} from 'mattermost-redux/selectors/entities/teams';
import {fetchUserThread} from 'features/threads/actions/fetch_user_thread';
import {getThreadPostFollowState} from 'features/threads/selectors/get_thread_post_follow_state';
import {getThreadReplyCount} from 'features/threads/selectors/get_thread_reply_count';
import {mark, Marks} from '@time-webkit/performance';
import {Posts} from 'mattermost-redux/constants';

const fetchThreadForThreadViewer = (rootPostId: Post['id']) =>
    createThunkAction('threads/actions/fetchThreadForThreadViewer', (dispatch, getState) => {
        const state = getState();
        const currentUserId = getCurrentUserId(state);
        const currentTeamId = getCurrentTeamId(state);
        const rootPost = getPost(state, rootPostId);
        const thread = selectThreadById(state, rootPostId);
        const isFollowingThread = getThreadPostFollowState(state, rootPostId);

        if (!rootPost) {
            return;
        }

        if (!isFollowingThread) {
            return;
        }

        const shouldFetchThread = (thread?.reply_count && thread?.reply_count > 0) ||
            (rootPost.state !== Posts.POST_DELETED && rootPost.reply_count > 0);

        if (shouldFetchThread) {
            return dispatch(
                fetchUserThread({
                    teamId: currentTeamId,
                    userId: currentUserId,
                    threadId: rootPostId,
                    extended: true,
                }),
            );
        }
    });

export function useUpdateThreadData(rootPostId: Post['id'], onThreadLoaded?: () => void) {
    const dispatch = useAppDispatch();
    const hasPost = useAppSelector((state) => Boolean(getPost(state, rootPostId)?.id));
    const hasReplies = useAppSelector((state) => getThreadReplyCount(state, rootPostId) > 0);
    const hasThread = useAppSelector((state) => Boolean(selectThreadById(state, rootPostId)?.id));
    const [loading, setLoading] = useState(hasPost && hasReplies);

    // Вызывается каждый раз при открытии нового треда или при переходе в другой тред
    useEffect(() => {
        if (!hasPost || !hasReplies) {
            return;
        }

        const abortController = new AbortController();
        const loadThread = async () => {
            const res = await dispatch(getPostThread(rootPostId, true, abortController.signal));

            if (res.data) {
                mark(
                    Marks.THREAD_LOADING_FINISHED,
                    {
                        detail: {
                            postCount: res.data.order.length,
                        },
                    },
                );
            }

            if (!res.canceled) {
                setLoading(false);
                onThreadLoaded?.();
            }
        };

        setLoading(true);
        mark(Marks.THREAD_LOADING_STARTED);
        loadThread();

        return () => abortController.abort();

        // Зависимости hasPost и hasReplies порождают лишние запросы на сервер при отправке именно первого сообщения в тред, это порождало следующую цепочку:
        // 1) при отправке сообщения сперва оно попадает в redux-state, что увеличивает replyCount данного треда
        // 2) replyCount влияет на рассчёт переменной hasReplies, изменение которой вызывало срабатывание данного useEffect
        // 3) useEffect порождает запрос getPostThread, хотя на беке нашего сообщения ещё нет (а значит запрашивается пустой тред)
        // 4) после вышеупомянутых пунктов мы отправляем POST запрос нашего сообщения на бек, и только тогда запрос getPostThread будет отдавать актуальные данные о participants
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rootPostId, dispatch]);

    useEffect(() => {
        if (hasThread) {
            return;
        }

        dispatch(fetchThreadForThreadViewer(rootPostId));
    }, [dispatch, hasThread, rootPostId]);

    return {loading};
}
